import React, { useState, useEffect, useRef } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  FormControl,
  FormLabel,
  Input,
  Textarea,
  Button,
  Grid,
  Select,
  Heading,
  IconButton,
  Box,
  Image,
  VStack,
} from "@chakra-ui/react";
import { X, Upload } from "lucide-react";
import { BranchType } from "@/types/index";

interface BranchModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (branch: FormData) => void
  editingBranch?: BranchType | null;
}

const BranchModal: React.FC<BranchModalProps> = ({ isOpen, onClose, onSave, editingBranch = null }) => {
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [description, setDescription] = useState("");
  const [status, setStatus] = useState<number>(1);
  const [imageId, setImageId] = useState<number | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const initialRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen && editingBranch) {
      setName(editingBranch.name);
      setLocation(editingBranch.location);
      setDescription(editingBranch.description || "");
      setStatus(editingBranch.status);
      setImageId(editingBranch.image_id);

      if (editingBranch.image) {
        setImagePreview(editingBranch.image.file_path || null);
      }
    } else if (!isOpen) {
      setName("");
      setLocation("");
      setDescription("");
      setStatus(1);
      setImageId(null);
      setImagePreview(null);
      setSelectedFile(null);
    }
  }, [isOpen, editingBranch]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setSelectedFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = () => {
    if (!name || !location) return;

    const formData = new FormData();
    formData.append('name', name);
    formData.append('location', location);
    formData.append('description', description);
    formData.append('status', status.toString());

    if (selectedFile) {
        formData.append('image', selectedFile);
    } else if (imageId && editingBranch) {
        formData.append('image_id', imageId.toString());
    }

    onSave(formData);
    onClose();
    };

  return (
    <>
      {isOpen && <div className="background-blur" />}

      <Modal isOpen={isOpen} onClose={onClose} isCentered size="lg" initialFocusRef={initialRef}>
        <ModalOverlay />
        <ModalContent borderRadius="0">
          <ModalHeader fontSize={{ base: "lgs", md: "xl" }}>
            {editingBranch ? "Edit Branch" : "New Branch"}
            <IconButton
              aria-label="Close Modal"
              icon={<X />}
              onClick={onClose}
              position="absolute"
              right={5}
              top={5}
              size="sm"
              variant="link"
              color="#0A1B39"
              _hover={{ color: "#DC3611" }}
              _focus={{ boxShadow: "none" }}
            />
          </ModalHeader>
          <Box borderBottom="1px solid #E7E8EB" pb={2} mb={4}></Box>
          <ModalBody>
            <div ref={initialRef} tabIndex={-1} />

            <Heading size={{ base: "sm", md: "sm" }} mb={4} fontWeight="semibold" color="#0A1B39">
              Branch Information
            </Heading>

            <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)" }} gap={4}>
              <FormControl isRequired>
                <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="400" color="#0A1B39">
                  Branch Name
                </FormLabel>
                <Input
                  placeholder="Enter branch name"
                  bg="white"
                  borderRadius="md"
                  borderColor="gray.200"
                  _hover={{ borderColor: "#DC3611" }}
                  _focus={{
                    borderColor: "#DC3611",
                    boxShadow: "0 0 0 1px #DC3611",
                  }}
                  fontSize={{ base: "xs", md: "sm" }}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="400" color="#0A1B39">
                  Location
                </FormLabel>
                <Input
                  placeholder="Enter location"
                  bg="white"
                  borderRadius="md"
                  borderColor="gray.200"
                  _hover={{ borderColor: "#DC3611" }}
                  _focus={{
                    borderColor: "#DC3611",
                    boxShadow: "0 0 0 1px #DC3611",
                  }}
                  fontSize={{ base: "xs", md: "sm" }}
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                />
              </FormControl>
            </Grid>

            <FormControl mt={4} isRequired>
              <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="400" color="#0A1B39">
                Status
              </FormLabel>
              <Select
                bg="white"
                borderRadius="md"
                borderColor="gray.200"
                _hover={{ borderColor: "#DC3611" }}
                _focus={{
                  borderColor: "#DC3611",
                  boxShadow: "0 0 0 1px #DC3611",
                }}
                fontSize={{ base: "xs", md: "sm" }}
                value={status}
                onChange={(e) => setStatus(Number(e.target.value))}
              >
                <option value={1}>Active</option>
                <option value={0}>Inactive</option>
              </Select>
            </FormControl>

            <FormControl mt={4}>
              <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="400" color="#0A1B39">
                Branch Image
              </FormLabel>
              <Input type="file" accept="image/*" ref={fileInputRef} display="none" onChange={handleFileSelect} />
              <VStack align="stretch" spacing={3}>
                {imagePreview && (
                  <Box borderWidth="1px" borderRadius="md" borderColor="gray.200" overflow="hidden" maxH="200px">
                    <Image src={imagePreview} alt="Branch preview" objectFit="cover" w="100%" h="100%" />
                  </Box>
                )}
                <Button
                  leftIcon={<Upload size={16} />}
                  onClick={() => fileInputRef.current?.click()}
                  bg="white"
                  borderWidth="1px"
                  borderColor="gray.200"
                  _hover={{ borderColor: "#DC3611" }}
                  fontSize={{ base: "xs", md: "sm" }}
                >
                  {imagePreview ? "Change Image" : "Upload Image"}
                </Button>
              </VStack>
            </FormControl>

            <FormControl mt={4}>
              <FormLabel fontSize={{ base: "xs", md: "sm" }} fontWeight="400" color="#0A1B39">
                Description
              </FormLabel>
              <Textarea
                placeholder="Enter branch description"
                bg="white"
                borderRadius="md"
                borderColor="gray.200"
                _hover={{ borderColor: "#DC3611" }}
                _focus={{
                  borderColor: "#DC3611",
                  boxShadow: "0 0 0 1px #DC3611",
                }}
                fontSize={{ base: "xs", md: "sm" }}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
            </FormControl>
          </ModalBody>
          <Box borderBottom="1px solid #E7E8EB" pb={2} mb={4}></Box>

          <ModalFooter gap={2} flexWrap="wrap">
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button bg="#DC3611" color="white" _hover={{ bg: "#bf3c1fff" }} onClick={handleSave} isDisabled={!name || !location}>
              Save Changes
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};

export default BranchModal;
